import { DisabledPrompt, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import DateSelect from "components/custom-ui/DateSelect";
import { FormContainer, FormRowView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { PrimaryButton, SecondaryButton, UICheckbox, UITextInput } from "components/ui";
import React from "react";
import "./mobile.css";

/**
 * @function SearchOptionsFormView
 * @return {React.Component}
 * @exported true
 * @description This is the main form view used in ActivityACHPage widgets 
 */
export default function SearchOptionsFormView(props) {
    const { data, onClear, onSearchChange } = useStateManager();
    const search = data.achSearch ?? {};
    const offsetAccounts = props.offsetAccounts ?? [];

    const offsetAccountItems = offsetAccounts.reduce((acc, account, index) => {
        const label = `${ account.number } ${ account.nickname }`;
        if(index === 0) {
          acc.push(<CustomSelectItem value="unbalanced">UNBALANCED</CustomSelectItem>);
        }
        acc.push(<CustomSelectItem value={ account.id }>{ label }</CustomSelectItem>);
        return acc;
      }, []);

    return (
        <div className="search-options-form-view">
            <FormContainer className="small-form-width">
                <FormRowView text="Company" className="company">
                    <CustomSelect value={ search.companyID } 
                    onChange={ onSearchChange ? onSearchChange("companyID") : undefined }>
                       <CustomSelectItem>&nbsp;</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                { data.isScheduledACHSearch && 
                <FormRowView text="Schedule" className="batch-name">
                    <UITextInput value={ search.schedule }
                    onChange={ onSearchChange ? onSearchChange("schedule") : undefined } />
                </FormRowView>
                }
                { !data.isScheduledACHSearch && 
                <FormRowView text="Batch" className="batch-name">
                    <UITextInput value={ search.batch }
                    onChange={ onSearchChange ? onSearchChange("batch") : undefined } />
                </FormRowView>
                }
                <FormRowView text="Status" className="status">
                    <CustomSelect value={ search.status } 
                    onChange={ onSearchChange ? onSearchChange("status") : undefined }>
                        <CustomSelectItem value="pending processing">Pending Processing</CustomSelectItem>
                        <CustomSelectItem value="rejected">Rejected</CustomSelectItem>
                        <CustomSelectItem value="canceled">Canceled</CustomSelectItem>
                        <CustomSelectItem value="processing">Processing</CustomSelectItem>
                        <CustomSelectItem value="processed">Processed</CustomSelectItem>
                        <CustomSelectItem value="canceled by system">Canceled By System</CustomSelectItem>
                        <CustomSelectItem value="processing failed">Processing Failed</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView className="prenote">
                    <UICheckbox label="Prenote" value={ search.prenote }
                    onChange={ onSearchChange ? onSearchChange("prenote") : undefined } />
                </FormRowView>
                <FormRowView text="Ref #" className="ref-number">
                    <UITextInput value={ search.referenceNumber }
                    onChange={ onSearchChange ? onSearchChange("referenceNumber") : undefined } />
                </FormRowView>
                <FormRowView text="Offset Account" className="offset-account">
                    <CustomSelect value={ search.offsetAccountID }
                    onChange={ onSearchChange ? onSearchChange("offsetAccountID") : undefined }>
                        {/*<CustomSelectItem value="unbalanced">UNBALANCED</CustomSelectItem>*/}
                        { offsetAccountItems }
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="Transaction Type" className="transaction-type">
                    <CustomSelect value={ search.transactionType } 
                    onChange={ onSearchChange ? onSearchChange("transactionType") : undefined }>
                        <CustomSelectItem value="ppd credit - consumer credit">PPD Credit - Consumer Credit</CustomSelectItem>
                        <CustomSelectItem value="ppd debit - consumer debit">PPD Debit - Consumer Debit</CustomSelectItem>
                        <CustomSelectItem value="ccd credit - non-consumer credit">CCD Credit - Non-Consumer Credit</CustomSelectItem>
                        <CustomSelectItem value="ccd debit - non-consumer debit">CCD Debit - Non-Consumer Debit</CustomSelectItem>
                        <CustomSelectItem value="tax payments">Tax Payments</CustomSelectItem>
                        <CustomSelectItem value="rck - re-presentation check">RCK - Re-Presentation Check</CustomSelectItem>
                        <CustomSelectItem value="web - consumer debit">WEB - Consumer Debit</CustomSelectItem>
                        <CustomSelectItem value="tel - telephone-initiated debit">TEL - Telephone-Initiated Debit</CustomSelectItem>
                        <CustomSelectItem value="ctx debit - corporate trade exchange debit">CTX Debit - Corporate Trade Exchange Debit</CustomSelectItem>
                        <CustomSelectItem value="ctx credit - corporate trade exchange credit">CTX Credit - Corporate Trade Exchange Credit</CustomSelectItem>
                        <CustomSelectItem value="child support payments">Child Support Payments</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="Effective Date" className="effective-date">
                    <CustomSelect value={ search.effectiveDate } 
                    onChange={ onSearchChange ? onSearchChange("effectiveDate") : undefined }>
                        <CustomSelectItem value="custom">Custom</CustomSelectItem>
                        <CustomSelectItem value="today">Today</CustomSelectItem>
                        <CustomSelectItem value="yesterday">Yesterday</CustomSelectItem>
                        <CustomSelectItem value="tomorrow">Tomorrow</CustomSelectItem>
                        <CustomSelectItem value="this week">This Week</CustomSelectItem>
                        <CustomSelectItem value="last week">Last Week</CustomSelectItem>
                        <CustomSelectItem value="next week">Next Week</CustomSelectItem>
                        <CustomSelectItem value="this month">This Month</CustomSelectItem>
                        <CustomSelectItem value="last month">Last Month</CustomSelectItem>
                        <CustomSelectItem value="next month">Next Month</CustomSelectItem>
                        <CustomSelectItem value="last 3 months">Last 3 Months</CustomSelectItem>
                        <CustomSelectItem value="last 6 months">Last 6 Months</CustomSelectItem>
                        <CustomSelectItem value="last 9 months">Last 9 Months</CustomSelectItem>
                        <CustomSelectItem value="this year">This Year</CustomSelectItem>
                        <CustomSelectItem value="last year">Last Year</CustomSelectItem>
                        <CustomSelectItem value="next year">Next Year</CustomSelectItem>
                        <CustomSelectItem value="all dates">All Dates</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="From:" className="from-date">
                    <DateSelect value={ search.effectiveDateFromDate } 
                    onChange={ onSearchChange ? onSearchChange("effectiveDateFromDate") : undefined } />
                </FormRowView>
                <FormRowView text="To:" className="to-date">
                    <DateSelect value={ search.effectiveDateToDate } 
                    onChange={ onSearchChange ? onSearchChange("effectiveDateToDate") : undefined } />
                </FormRowView>
                <FormRowView text="Last Updated Date" className="last-updated-date">
                <CustomSelect value={ search.lastUpdatedDate } 
                    onChange={ onSearchChange ? onSearchChange("lastUpdatedDate") : undefined }>
                        <CustomSelectItem value="custom">Custom</CustomSelectItem>
                        <CustomSelectItem value="today">Today</CustomSelectItem>
                        <CustomSelectItem value="yesterday">Yesterday</CustomSelectItem>
                        <CustomSelectItem value="tomorrow">Tomorrow</CustomSelectItem>
                        <CustomSelectItem value="this week">This Week</CustomSelectItem>
                        <CustomSelectItem value="last week">Last Week</CustomSelectItem>
                        <CustomSelectItem value="next week">Next Week</CustomSelectItem>
                        <CustomSelectItem value="this month">This Month</CustomSelectItem>
                        <CustomSelectItem value="last month">Last Month</CustomSelectItem>
                        <CustomSelectItem value="next month">Next Month</CustomSelectItem>
                        <CustomSelectItem value="last 3 months">Last 3 Months</CustomSelectItem>
                        <CustomSelectItem value="last 6 months">Last 6 Months</CustomSelectItem>
                        <CustomSelectItem value="last 9 months">Last 9 Months</CustomSelectItem>
                        <CustomSelectItem value="this year">This Year</CustomSelectItem>
                        <CustomSelectItem value="last year">Last Year</CustomSelectItem>
                        <CustomSelectItem value="next year">Next Year</CustomSelectItem>
                        <CustomSelectItem value="all dates">All Dates</CustomSelectItem>
                </CustomSelect>
                </FormRowView>
                <FormRowView text="From:" className="from-date">
                    <DateSelect value={ search.lastUpdatedDateFromDate } 
                    onChange={ onSearchChange ? onSearchChange("lastUpdatedDateFromDate") : undefined } />
                </FormRowView>
                <FormRowView text="To:" className="to-date">
                    <DateSelect value={ search.lastUpdatedDateToDate } 
                    onChange={ onSearchChange ? onSearchChange("lastUpdatedDateToDate") : undefined } />
                </FormRowView>
                <FormButtonRow>
                    <SecondaryButton text="Clear" onClick= { onClear } />
                    <DisabledPrompt>
                        <PrimaryButton text="Search" />
                    </DisabledPrompt>
                </FormButtonRow>
            </FormContainer>
        </div>
    )
}


/**
 * @function ScheduledSearchOptionsFormView
 * @return {React.Component}
 * @exported true
 * @description This is the main form view used in ActivityACHPage widgets that are. 
 * This component is not currently being used since component "SearchOptionsFormView" may be sufficient for now.
 */
export function ScheduledSearchOptionsFormView(props) {
    return (
        <div className="search-options-form-view">
            <FormContainer className="small-form-width">
                <FormRowView text="Company" className="company">
                    <CustomSelect>
                        <CustomSelectItem></CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="Schedule" className="batch-name">
                    <UITextInput />
                </FormRowView>
                <FormRowView text="Schedule Status" className="status">
                    <CustomSelect value="processing">
                        <CustomSelectItem>Pending Approval</CustomSelectItem>
                        <CustomSelectItem>Approved</CustomSelectItem>
                        <CustomSelectItem>Rejected</CustomSelectItem>
                        <CustomSelectItem>Canceled</CustomSelectItem>
                        <CustomSelectItem value="processing">Processing</CustomSelectItem>
                        <CustomSelectItem>Processed</CustomSelectItem>
                        <CustomSelectItem>Canceled By System</CustomSelectItem>
                        <CustomSelectItem>Processing Failed</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView className="prenote">
                    <UICheckbox label="Prenote" />
                </FormRowView>
                <FormRowView text="Ref #" className="ref-number">
                    <UITextInput />
                </FormRowView>
                <FormRowView text="Offset Account" className="offset-account">
                    <CustomSelect>
                        <CustomSelectItem>[Offset Accounts]</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="Transaction Type" className="transaction-type">
                    <CustomSelect>
                        <CustomSelectItem>PPD Credit - Consumer Credit</CustomSelectItem>
                        <CustomSelectItem>PPD Debit - Consumer Debit</CustomSelectItem>
                        <CustomSelectItem>CCD Credit - Non-Consumer Credit</CustomSelectItem>
                        <CustomSelectItem>CCD Debit - Non-Consumer Debit</CustomSelectItem>
                        <CustomSelectItem>Tax Payments</CustomSelectItem>
                        <CustomSelectItem>RCK - Re-Presentation Check</CustomSelectItem>
                        <CustomSelectItem>WEB - Consumer Debit</CustomSelectItem>
                        <CustomSelectItem>TEL - Telephone-Initiated Debit</CustomSelectItem>
                        <CustomSelectItem>CTX Debit - Corporate Trade Exchange Debit</CustomSelectItem>
                        <CustomSelectItem>CTX Credit - Corporate Trade Exchange Credit</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="Effective Date" className="effective-date">
                    <CustomSelect value="All Dates">
                        <CustomSelectItem value="All Dates">All Dates</CustomSelectItem>
                        <CustomSelectItem value="Custom">Custom</CustomSelectItem>
                        <CustomSelectItem value="Today">Today</CustomSelectItem>
                        <CustomSelectItem value="Yesterday">Yesterday</CustomSelectItem>
                        <CustomSelectItem value="Tomorrow">Tomorrow</CustomSelectItem>
                        <CustomSelectItem value="This Week">This Week</CustomSelectItem>
                        <CustomSelectItem value="Last Week">Last Week</CustomSelectItem>
                        <CustomSelectItem value="Next Week">Next Week</CustomSelectItem>
                        <CustomSelectItem value="This Month">This Month</CustomSelectItem>
                        <CustomSelectItem value="Last Month">Last Month</CustomSelectItem>
                        <CustomSelectItem value="Next Month">Next Month</CustomSelectItem>
                        <CustomSelectItem value="Last 3 Months">Last 3 Months</CustomSelectItem>
                        <CustomSelectItem value="Last 6 Months">Last 6 Months</CustomSelectItem>
                        <CustomSelectItem value="Last 9 Months">Last 9 Months</CustomSelectItem>
                        <CustomSelectItem value="This Year">This Year</CustomSelectItem>
                        <CustomSelectItem value="Last Year">Last Year</CustomSelectItem>
                        <CustomSelectItem value="Next Year">Next Year</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="From:" className="from-date">
                    <DateSelect />
                </FormRowView>
                <FormRowView text="To:" className="to-date">
                    <DateSelect />
                </FormRowView>
                <FormRowView text="Last Updated Date" className="last-updated-date">
                    <CustomSelect value="All Dates">
                        <CustomSelectItem value="All Dates" >All Dates</CustomSelectItem>
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="From:" className="from-date">
                    <DateSelect />
                </FormRowView>
                <FormRowView text="To:" className="to-date">
                    <DateSelect />
                </FormRowView>
                <FormButtonRow>
                    <DisabledPrompt>
                        <SecondaryButton text="Clear" />
                    </DisabledPrompt>
                    <DisabledPrompt>
                        <PrimaryButton text="Search" />
                    </DisabledPrompt>
                </FormButtonRow>
            </FormContainer>
        </div>
    )
}
